import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  App,
  Button,
  Card,
  Col,
  Divider,
  Form,
  Input,
  InputNumber,
  Modal,
  Row,
  Select,
  Space,
  Tag,
  Typography,
} from 'antd';
import {
  AuditOutlined,
  BankOutlined,
  CalendarOutlined,
  ClockCircleOutlined,
  DollarOutlined,
  InfoCircleOutlined,
  SaveOutlined,
  SafetyCertificateOutlined,
  TeamOutlined,
} from '@ant-design/icons';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { Link } from 'react-router-dom';
import { EnterprisePageHeader, WorkspaceLayout } from '@/components/enterprise';
import api from '@/services/api.service';
import hrmsService, { hrmsKeys } from '@/services/hrms.service';
import {
  EOSB_FORMULA_OPTIONS,
  FORGOTTEN_CHECKOUT_POLICY_OPTIONS,
  OT_EXCESS_OPTIONS,
  POLICY_PACK_OPTIONS,
  COMPLIANCE_JURISDICTION_OPTIONS,
  VALUATION_METHOD_OPTIONS,
  isIndiaHrmsPack,
} from './shared/hrmsPolicyOptions';
import WorkWeekSelect from './shared/WorkWeekSelect';
import WorkWeekModal from './shared/WorkWeekModal';

const { Text, Paragraph } = Typography;

type CoaAccount = {
  id: string;
  account_code?: string;
  account_name?: string;
  is_active?: boolean;
  is_group?: boolean;
};

const sectionCardStyle: React.CSSProperties = { height: '100%', marginBottom: 0 };

const HrmsSetupPage: React.FC = () => {
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const [ensuring, setEnsuring] = useState(false);
  const [ensuringWorkflows, setEnsuringWorkflows] = useState(false);
  const [carryRunning, setCarryRunning] = useState(false);
  const [accrualRunning, setAccrualRunning] = useState(false);
  const [carryNote, setCarryNote] = useState<string | null>(null);
  const [workWeeksOpen, setWorkWeeksOpen] = useState(false);

  const { data: policy = {}, isLoading } = useQuery({
    queryKey: hrmsKeys.policy(),
    queryFn: () => hrmsService.getPolicy(),
  });

  const watchedPack = Form.useWatch('hrms.policy_pack', form);
  const indiaPack = isIndiaHrmsPack(watchedPack || policy['hrms.policy_pack']);

  const { data: coa = [] } = useQuery({
    queryKey: ['chart-of-accounts', 'hrms-setup'],
    queryFn: async () => {
      const res = await api.get<CoaAccount[]>('/finance/chart-of-accounts');
      const rows = Array.isArray(res.data) ? res.data : (res.data as any)?.data ?? [];
      return (rows as CoaAccount[]).filter((a) => a.is_active !== false && a.is_group !== true);
    },
    staleTime: 60_000,
  });

  const coaOptions = useMemo(
    () =>
      coa.map((a) => ({
        value: a.id,
        label: `${a.account_code ?? ''} — ${a.account_name ?? a.id}`.trim(),
      })),
    [coa]
  );

  useEffect(() => {
    const numericKeys = [
      'hrms.eosb.min_service_years',
      'hrms.eosb.india_cap_amount',
      'hrms.leave.annual_days',
      'hrms.ot.monthly_cap_hours',
      'hrms.late.grace_minutes',
      'hrms.punch.auto_close_grace_minutes',
      'hrms.punch.auto_check_in_dwell_seconds',
      'hrms.probation.days',
    ];
    const values: Record<string, unknown> = { ...policy };
    for (const k of numericKeys) {
      if (values[k] != null && values[k] !== '') values[k] = Number(values[k]);
    }
    if (values['hrms.punch.auto_check_in_enabled'] == null || values['hrms.punch.auto_check_in_enabled'] === '') {
      values['hrms.punch.auto_check_in_enabled'] = 'false';
    } else {
      values['hrms.punch.auto_check_in_enabled'] = String(values['hrms.punch.auto_check_in_enabled']);
    }
    for (const k of [
      'hrms.gl.salary_expense_account_id',
      'hrms.gl.salary_payable_account_id',
      'hrms.gl.employee_loan_account_id',
      'hrms.gl.leave_expense_account_id',
      'hrms.gl.leave_liability_account_id',
      'hrms.gl.eosb_expense_account_id',
      'hrms.gl.eosb_liability_account_id',
      'hrms.gl.eosb_oci_account_id',
      'hrms.gl.epf_employer_expense_account_id',
      'hrms.gl.epf_employer_liability_account_id',
      'hrms.gl.esi_employer_expense_account_id',
      'hrms.gl.esi_employer_liability_account_id',
    ]) {
      if (!values[k]) values[k] = undefined;
    }
    form.setFieldsValue(values);
  }, [policy, form]);

  const onSave = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      const payload: Record<string, string> = {};
      for (const [k, v] of Object.entries(values)) {
        if (!k.startsWith('hrms.')) continue;
        payload[k] = v == null || v === undefined ? '' : String(v);
      }
      await hrmsService.savePolicy(payload);
      message.success('HRMS policy saved for this company');
      queryClient.invalidateQueries({ queryKey: hrmsKeys.policy() });
    } catch (e: any) {
      if (e?.errorFields) return;
      message.error(e?.message || 'Failed to save policy');
    } finally {
      setSaving(false);
    }
  };

  const onEnsureDefaults = async () => {
    try {
      setEnsuring(true);
      const next = await hrmsService.ensureDefaults();
      message.success(
        indiaPack
          ? 'India pack defaults applied for any missing keys (existing values kept)'
          : 'UAE Federal Labour Law defaults applied (missing keys only)'
      );
      queryClient.invalidateQueries({ queryKey: hrmsKeys.policy() });
      if (next && typeof next === 'object') form.setFieldsValue(next as Record<string, string>);
    } catch {
      message.error('Failed to ensure defaults');
    } finally {
      setEnsuring(false);
    }
  };

  const onEnsureApprovalWorkflows = async () => {
    try {
      setEnsuringWorkflows(true);
      const result = await hrmsService.ensureApprovalWorkflows();
      const workflows = Array.isArray(result?.workflows) ? result.workflows : [];
      const created = workflows.filter((w: { action?: string }) => w.action === 'created').length;
      const exists = workflows.filter(
        (w: { action?: string }) => w.action === 'exists' || w.action === 'reactivated'
      ).length;
      const total = result?.count ?? workflows.length;
      message.success(
        `HR approval workflows ready: ${total} total (${created} created, ${exists} already exist)`
      );
    } catch (e: any) {
      message.error(e?.response?.data?.message || e?.message || 'Failed to ensure approval workflows');
    } finally {
      setEnsuringWorkflows(false);
    }
  };

  const onYearEndCarry = () => {
    const year = dayjs().year();
    Modal.confirm({
      title: 'Run year-end leave carry-forward?',
      content: `This posts leave balance carry for calendar year ${year} into the next year. The action is audited.`,
      okText: 'Run carry-forward',
      onOk: async () => {
        setCarryRunning(true);
        try {
          const result = await hrmsService.runLeaveYearEndCarry({ year });
          const note =
            result?.message ||
            result?.audit_note ||
            `Year-end leave carry-forward completed for ${year} at ${dayjs().format('DD-MMM-YYYY HH:mm')}.`;
          setCarryNote(note);
          message.success('Year-end leave carry-forward completed');
        } catch {
          message.error('Year-end carry-forward failed');
        } finally {
          setCarryRunning(false);
        }
      },
    });
  };

  const packLabel =
    POLICY_PACK_OPTIONS.find((o) => o.value === policy['hrms.policy_pack'])?.label ||
    policy['hrms.policy_pack'] ||
    'Not set';

  return (
    <WorkspaceLayout
      header={
        <EnterprisePageHeader
          documentLabel="HRMS Setup"
          subtitle={
            indiaPack
              ? 'Company labour policy — India private-sector / school baseline (POGA gratuity + EL/CL/SL); confirm state overlays locally'
              : 'Company labour policy — defaults follow UAE Federal Decree-Law 33/2021; override per company as needed'
          }
          statusTag={<Tag color="blue">Company policy</Tag>}
          primaryActions={
            <Space wrap>
              <Button loading={ensuring} icon={<SafetyCertificateOutlined />} onClick={onEnsureDefaults}>
                {indiaPack ? 'Apply pack defaults' : 'Apply UAE defaults'}
              </Button>
              <Button
                loading={ensuringWorkflows}
                icon={<AuditOutlined />}
                onClick={onEnsureApprovalWorkflows}
              >
                Ensure HR approval workflows
              </Button>
              <Button type="primary" loading={saving} icon={<SaveOutlined />} onClick={onSave}>
                Save policy
              </Button>
            </Space>
          }
        />
      }
    >
      <Alert
        type="info"
        showIcon
        icon={<InfoCircleOutlined />}
        style={{ marginBottom: 16 }}
        message="How HRMS policy works"
        description={
          <span>
            These settings drive leave entitlement seeding, overtime caps, late calculation, probation length, and
            end-of-service (EOSB / gratuity) for <strong>this company only</strong>. Related masters —{' '}
            <Link to="/hr/holidays">holiday calendars</Link>, <Link to="/hr/shifts">shifts</Link>,{' '}
            <Link to="/hr/locations">locations</Link>, <Link to="/hr/grades">grades</Link>,{' '}
            <Link to="/hr/salary-heads">salary heads</Link> — refine day-to-day operations. Use{' '}
            <strong>{indiaPack ? 'Apply pack defaults' : 'Apply UAE defaults'}</strong> to fill any
            missing keys without overwriting values you already saved.
          </span>
        }
      />

      <Alert
        type="warning"
        showIcon
        style={{ marginBottom: 16 }}
        message="Approval Runtime workflows required for Submit"
        description="Approval Runtime workflows must exist for this company so HR Submit actions route via ApprovalPanel. Use Ensure HR approval workflows if Submit does not open an approval instance."
      />

      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={24} md={8}>
          <Card size="small">
            <Text type="secondary">Active policy pack</Text>
            <div style={{ marginTop: 4 }}>
              <Text strong>{packLabel}</Text>
            </div>
          </Card>
        </Col>
        <Col xs={24} md={8}>
          <Card size="small">
            <Text type="secondary">Annual leave (policy)</Text>
            <div style={{ marginTop: 4 }}>
              <Text strong>{policy['hrms.leave.annual_days'] ?? '—'} days / year</Text>
            </div>
          </Card>
        </Col>
        <Col xs={24} md={8}>
          <Card size="small">
            <Text type="secondary">OT monthly cap</Text>
            <div style={{ marginTop: 4 }}>
              <Text strong>{policy['hrms.ot.monthly_cap_hours'] ?? '—'} hours</Text>
            </div>
          </Card>
        </Col>
      </Row>

      <Form form={form} layout="vertical" disabled={isLoading}>
        <Row gutter={[16, 16]}>
          <Col xs={24} lg={12}>
            <Card
              loading={isLoading}
              style={sectionCardStyle}
              title={
                <Space>
                  <SafetyCertificateOutlined />
                  <span>Labour law pack</span>
                </Space>
              }
            >
              <Paragraph type="secondary" style={{ marginTop: 0 }}>
                Selects the labour baseline used when seeding leave types and interpreting end-of-service gratuity.
                Company overrides below still apply on top of the pack. DAV / India schools should use the India pack —
                not UAE Federal Law.
              </Paragraph>
              {indiaPack ? (
                <Alert
                  type="info"
                  showIcon
                  style={{ marginBottom: 12 }}
                  message="India pack active"
                  description="Seeds EL/CL/SL leave types, POGA 1972 gratuity ((15/26)×wages×years, min 5 yrs), Mon–Sat work week, and STATUTORY_INDIA valuation. State Shops Acts, PF/ESI engines, and school holiday calendars still need local confirmation."
                />
              ) : null}
              <Form.Item
                name="hrms.policy_pack"
                label="Policy pack"
                rules={[{ required: true, message: 'Select a policy pack' }]}
                help="Stored as a system code; choose the readable law name shown here."
              >
                <Select options={[...POLICY_PACK_OPTIONS]} optionFilterProp="label" showSearch />
              </Form.Item>
              <Form.Item
                name="hrms.work_week"
                label="Standard work week"
                rules={[{ required: true }]}
                help={
                  <span>
                    Weekly off days used by timesheet auto-generation when a location has no work week of its own. Not in
                    the list? Use <strong>Add work week</strong> at the bottom of the dropdown, or{' '}
                    <Typography.Link onClick={() => setWorkWeeksOpen(true)}>manage work weeks</Typography.Link>.
                  </span>
                }
              >
                <WorkWeekSelect />
              </Form.Item>
              <Form.Item
                name="hrms.probation.days"
                label="Probation period (days)"
                rules={[{ required: true }]}
                help={
                  indiaPack
                    ? 'India private-sector / school operational default is often 90 days (company policy). Confirm appointment letter terms.'
                    : 'UAE private-sector maximum is typically 180 days (6 months). Used on new employment contracts as the default probation length.'
                }
              >
                <InputNumber min={0} max={365} style={{ width: '100%' }} addonAfter="days" />
              </Form.Item>
            </Card>
          </Col>

          <Col xs={24} lg={12}>
            <Card
              loading={isLoading}
              style={sectionCardStyle}
              title={
                <Space>
                  <CalendarOutlined />
                  <span>Leave entitlement</span>
                </Space>
              }
            >
              <Paragraph type="secondary" style={{ marginTop: 0 }}>
                Controls default earned/annual leave days when you apply pack defaults (creates missing leave types).
                Existing approved leave applications are not rewritten.
              </Paragraph>
              <Form.Item
                name="hrms.leave.annual_days"
                label={indiaPack ? 'Earned / privilege leave entitlement' : 'Annual leave entitlement'}
                rules={[{ required: true }]}
                help={
                  indiaPack
                    ? 'India operational baseline seeds 15 earned/privilege days (+ separate CL/SL). State Shops Acts and school board policy may require different entitlements.'
                    : 'UAE Federal Law default is 30 calendar days after completing the probation / qualifying period. Company policy may grant more.'
                }
              >
                <InputNumber min={0} max={90} style={{ width: '100%' }} addonAfter="days / year" />
              </Form.Item>
              <Alert
                type="success"
                showIcon
                message="Next steps"
                description={
                  <span>
                    Configure detailed rules on <Link to="/hr/leave-types">Leave types</Link> (entitlement, accrual,
                    max carry, encashable, unpaid). Enter go-live balances on{' '}
                    <Link to="/hr/leave-opening-balances">Leave openings</Link>. Holidays:{' '}
                    <Link to="/hr/holidays">Holiday calendars</Link>. Locations:{' '}
                    <Link to="/hr/locations">work locations</Link>.
                  </span>
                }
              />
              <Divider />
              <Alert
                type="info"
                showIcon
                style={{ marginBottom: 12 }}
                message="Accrual processing moved"
                description={
                  <span>
                    Preview, process, and review leave and EOSB accruals on{' '}
                    <Link to="/hr/accruals">Accrual Management</Link>. Setup remains for policy
                    configuration only.
                  </span>
                }
              />
              <Space direction="vertical" style={{ width: '100%' }}>
                <Button
                  loading={accrualRunning}
                  onClick={async () => {
                    setAccrualRunning(true);
                    try {
                      const now = dayjs();
                      const r = await hrmsService.runLeaveAccrual({
                        year: now.year(),
                        month: now.month() + 1,
                      });
                      const posted = r?.accrued_rows ?? r?.count ?? 0;
                      const skipped = r?.skipped_duplicate ?? 0;
                      message.success(
                        `Monthly accrual ${r?.period_label || ''}: posted ${posted} row(s)` +
                          (skipped ? `, ${skipped} already posted` : '')
                      );
                    } catch (e: any) {
                      message.error(e?.response?.data?.message || e?.message || 'Accrual failed');
                    } finally {
                      setAccrualRunning(false);
                    }
                  }}
                >
                  Quick-run current month leave accrual
                </Button>
                <Button loading={carryRunning} onClick={onYearEndCarry}>
                  Run year-end leave carry-forward
                </Button>
                {carryNote && (
                  <Alert type="info" showIcon message="Audit note" description={carryNote} />
                )}
              </Space>
            </Card>
          </Col>

          <Col xs={24} lg={12}>
            <Card
              loading={isLoading}
              style={sectionCardStyle}
              title={
                <Space>
                  <ClockCircleOutlined />
                  <span>Overtime & attendance</span>
                </Space>
              }
            >
              <Paragraph type="secondary" style={{ marginTop: 0 }}>
                Applied when OT requests are approved and when daily attendance late minutes are computed against the
                employee’s assigned shift.
              </Paragraph>
              <Form.Item
                name="hrms.ot.monthly_cap_hours"
                label="Overtime monthly cap"
                rules={[{ required: true }]}
                help="Hours above this cap in a calendar month are treated as “excess” and handled per the disposition below (UAE practice often limits OT)."
              >
                <InputNumber min={0} max={200} style={{ width: '100%' }} addonAfter="hours / month" />
              </Form.Item>
              <Form.Item
                name="hrms.ot.excess_disposition"
                label="What to do with excess OT"
                rules={[{ required: true }]}
                help="TOIL banks time as leave; Pay includes it in payroll; Defer holds it; Discard drops it."
              >
                <Select options={[...OT_EXCESS_OPTIONS]} optionFilterProp="label" />
              </Form.Item>
              <Form.Item
                name="hrms.late.grace_minutes"
                label="Late arrival grace"
                rules={[{ required: true }]}
                help="Minutes after shift start before attendance marks the employee late. Can be overridden per shift on the Shifts master."
              >
                <InputNumber min={0} max={120} style={{ width: '100%' }} addonAfter="minutes" />
              </Form.Item>
              <Divider style={{ margin: '8px 0 16px' }} />
              <Text strong style={{ display: 'block', marginBottom: 8 }}>
                Mobile punch — forgotten check-out
              </Text>
              <Paragraph type="secondary" style={{ marginTop: 0 }}>
                When an employee checks in on mobile and leaves without checking out, this company policy decides what
                happens next. HR can still correct the day on the attendance register for any policy.
              </Paragraph>
              <Form.Item
                name="hrms.punch.forgotten_checkout_policy"
                label="Forgotten check-out policy"
                rules={[{ required: true }]}
                help="Applies to ESS mobile punch (Web and Mobile share the same server rules)."
              >
                <Select options={[...FORGOTTEN_CHECKOUT_POLICY_OPTIONS]} optionFilterProp="label" />
              </Form.Item>
              <Form.Item noStyle shouldUpdate={(prev, cur) => prev['hrms.punch.forgotten_checkout_policy'] !== cur['hrms.punch.forgotten_checkout_policy']}>
                {() => {
                  const pol = form.getFieldValue('hrms.punch.forgotten_checkout_policy');
                  const showGrace = pol === 'auto_close_at_shift_end';
                  return (
                    <Form.Item
                      name="hrms.punch.auto_close_grace_minutes"
                      label="Auto-close grace after shift end"
                      rules={[{ required: showGrace }]}
                      help={
                        showGrace
                          ? 'Minutes after shift end before the system closes an open check-in.'
                          : 'Only used when the policy is “Auto-close at shift end”.'
                      }
                    >
                      <InputNumber
                        min={0}
                        max={240}
                        disabled={!showGrace}
                        style={{ width: '100%' }}
                        addonAfter="minutes"
                      />
                    </Form.Item>
                  );
                }}
              </Form.Item>
              <Divider style={{ margin: '8px 0 16px' }} />
              <Text strong style={{ display: 'block', marginBottom: 8 }}>
                Mobile punch — automatic check-in
              </Text>
              <Paragraph type="secondary" style={{ marginTop: 0 }}>
                When enabled, the ORDAN mobile app may check the employee in automatically after they enter the
                geofence for their assigned work location (OS location permissions required). Check-out remains
                manual. Site coordinates and radius are configured on{' '}
                <Link to="/hr/locations">Locations</Link>.
              </Paragraph>
              <Form.Item
                name="hrms.punch.auto_check_in_enabled"
                label="Automatic check-in"
                rules={[{ required: true }]}
                help="Off by default. Employees still opt in on the mobile punch screen and must grant Always location access."
              >
                <Select
                  options={[
                    { value: 'false', label: 'Disabled — manual check-in only (default)' },
                    { value: 'true', label: 'Enabled — allow geofence automatic check-in (IN only)' },
                  ]}
                />
              </Form.Item>
              <Form.Item
                name="hrms.punch.auto_check_in_dwell_seconds"
                label="Dwell time before auto check-in"
                rules={[{ required: true }]}
                help="Seconds the employee should remain inside the fence before an automatic check-in is attempted (reduces drive-by false punches)."
              >
                <InputNumber min={15} max={600} style={{ width: '100%' }} addonAfter="seconds" />
              </Form.Item>
              <Divider style={{ margin: '8px 0 16px' }} />
              <Text type="secondary">
                Define working hours on <Link to="/hr/shifts">Shifts</Link>, then assign them on contracts / employees.
              </Text>
            </Card>
          </Col>

          <Col xs={24} lg={12}>
            <Card
              loading={isLoading}
              style={sectionCardStyle}
              title={
                <Space>
                  <TeamOutlined />
                  <span>End of service (EOSB / gratuity)</span>
                </Space>
              }
            >
              <Paragraph type="secondary" style={{ marginTop: 0 }}>
                {indiaPack ? (
                  <>
                    India Payment of Gratuity Act, 1972 (operational): amount = (15/26) × wages × completed years;
                    default eligibility 5 years; ceiling from India gratuity cap setting. Include DA in wages when
                    marked on salary heads. Unpaid leave reduces qualifying service approximately.
                  </>
                ) : (
                  <>
                    UAE Federal Decree-Law 33/2021: gratuity on <strong>basic salary only</strong> — 21 days’ wage per
                    year for the first 5 years of service, 30 days thereafter, capped at 2 years’ wages. Unpaid leave
                    reduces qualifying service.
                  </>
                )}
              </Paragraph>
              <Form.Item
                name="hrms.eosb.formula"
                label="EOSB / gratuity calculation formula"
                rules={[{ required: true }]}
                help="Select the statutory formula. Custom formulas can be added later via policy packs."
              >
                <Select options={[...EOSB_FORMULA_OPTIONS]} optionFilterProp="label" />
              </Form.Item>
              <Form.Item
                name="hrms.eosb.min_service_years"
                label="Minimum service for EOSB / gratuity"
                rules={[{ required: true }]}
                help={
                  indiaPack
                    ? 'Employees below this continuous service length receive zero gratuity (India POGA default: 5 years).'
                    : 'Employees below this continuous service length receive zero gratuity under the selected formula (UAE default: 1 year).'
                }
              >
                <InputNumber min={0} max={10} step={0.5} style={{ width: '100%' }} addonAfter="years" />
              </Form.Item>
              <Alert
                type="warning"
                showIcon
                message="Salary heads matter"
                description={
                  <span>
                    Mark which pay components count as basic / EOSB base on{' '}
                    <Link to="/hr/salary-heads">Salary heads</Link>. Incorrect flags will under- or over-state
                    gratuity.
                  </span>
                }
              />
            </Card>
          </Col>

          <Col xs={24}>
            <Card
              loading={isLoading}
              title={
                <Space>
                  <DollarOutlined />
                  <span>Payroll → Finance GL handoff</span>
                </Space>
              }
            >
              <Alert
                type="info"
                showIcon
                style={{ marginBottom: 16 }}
                message="Payable is company-level; expense is primarily per salary head"
                description={
                  <span>
                    Map each earning’s expense account on <Link to="/hr/salary-heads">Salary heads</Link> (including
                    separate OT Normal / Friday / Holiday heads with rates). The company expense account below is only
                    the fallback for unmapped heads. Salary payable remains here for the credit side of the payroll
                    handoff.
                  </span>
                }
              />
              <Row gutter={16}>
                <Col xs={24} md={12}>
                  <Form.Item
                    name="hrms.gl.salary_expense_account_id"
                    label="Default salary expense account (fallback)"
                    help="Used only when a payroll earning has no per-head expense account."
                  >
                    <Select
                      allowClear
                      showSearch
                      optionFilterProp="label"
                      placeholder="Select COA account"
                      options={coaOptions}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item
                    name="hrms.gl.salary_payable_account_id"
                    label="Salary payable account"
                    help="Credit side until paid (typically a liability / accrued payroll leaf account)."
                  >
                    <Select
                      allowClear
                      showSearch
                      optionFilterProp="label"
                      placeholder="Select COA account"
                      options={coaOptions}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item
                    name="hrms.gl.employee_loan_account_id"
                    label="Employee loans / advances account"
                    help="Credited with loan instalments deducted in payroll (asset / receivable leaf account)."
                  >
                    <Select
                      allowClear
                      showSearch
                      optionFilterProp="label"
                      placeholder="Select COA account"
                      options={coaOptions}
                    />
                  </Form.Item>
                </Col>
              </Row>
              <Divider orientation="left" plain>
                Leave &amp; EOSB accrual accounting
              </Divider>
              <Alert
                type="info"
                showIcon
                style={{ marginBottom: 16 }}
                message="Statutory entitlement ≠ accounting liability by design"
                description={
                  <span>
                    Map expense / liability accounts used by Accrual Management → Accounting (Draft JV handoff). Leave
                    settlements prefer the leave liability account so utilization does not re-expense. OCI is reserved
                    for future IAS 19 remeasurements. Configure valuation method and run reconciliation from{' '}
                    <Link to="/hr/accruals">Accrual Management</Link>.
                  </span>
                }
              />
              <Row gutter={16}>
                <Col xs={24} md={12}>
                  <Form.Item name="hrms.gl.leave_expense_account_id" label="Leave accrual expense">
                    <Select allowClear showSearch optionFilterProp="label" placeholder="Select COA" options={coaOptions} />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item name="hrms.gl.leave_liability_account_id" label="Leave accrual liability">
                    <Select allowClear showSearch optionFilterProp="label" placeholder="Select COA" options={coaOptions} />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item name="hrms.gl.eosb_expense_account_id" label="EOSB provision expense">
                    <Select allowClear showSearch optionFilterProp="label" placeholder="Select COA" options={coaOptions} />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item name="hrms.gl.eosb_liability_account_id" label="EOSB provision liability">
                    <Select allowClear showSearch optionFilterProp="label" placeholder="Select COA" options={coaOptions} />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item
                    name="hrms.gl.eosb_oci_account_id"
                    label="EOSB OCI (optional)"
                    help="For IAS 19 remeasurements when actuarial import is enabled — unused under STATUTORY_UAE."
                  >
                    <Select allowClear showSearch optionFilterProp="label" placeholder="Select COA" options={coaOptions} />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item
                    name="hrms.accounting.leave_valuation_method"
                    label="Leave valuation method"
                    initialValue={indiaPack ? 'STATUTORY_INDIA' : 'STATUTORY_UAE'}
                  >
                    <Select options={[...VALUATION_METHOD_OPTIONS]} />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item
                    name="hrms.accounting.eosb_valuation_method"
                    label="EOSB / gratuity valuation method"
                    initialValue={indiaPack ? 'STATUTORY_INDIA' : 'STATUTORY_UAE'}
                  >
                    <Select options={[...VALUATION_METHOD_OPTIONS]} />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item
                    name="hrms.compliance.jurisdiction"
                    label="Compliance jurisdiction"
                    initialValue={indiaPack ? 'INDIA_CENTRAL' : 'UAE_MAINLAND'}
                  >
                    <Select options={[...COMPLIANCE_JURISDICTION_OPTIONS]} />
                  </Form.Item>
                </Col>
                {indiaPack ? (
                  <Col xs={24} md={12}>
                    <Form.Item
                      name="hrms.eosb.india_cap_amount"
                      label="India gratuity ceiling (₹)"
                      help="Payment of Gratuity Act operational cap (default ₹20,00,000)."
                    >
                      <InputNumber min={0} style={{ width: '100%' }} addonBefore="₹" />
                    </Form.Item>
                  </Col>
                ) : null}
                <Col xs={24} md={12}>
                  <Form.Item
                    name="hrms.accounting.recon_blocks_close"
                    label="Recon variance blocks close flag"
                    initialValue="true"
                    help="When true, unexplained HRMS↔GL variance sets block_financial_close on recon runs."
                  >
                    <Select
                      options={[
                        { value: 'true', label: 'Yes — flag close gate' },
                        { value: 'false', label: 'No — observe only' },
                      ]}
                    />
                  </Form.Item>
                </Col>
              </Row>
              <Text type="secondary">
                Manage accounts in <Link to="/fin/gl/chart-of-accounts">Finance → Chart of accounts</Link>.
              </Text>
            </Card>
          </Col>

          <Col xs={24}>
            <Card
              loading={isLoading}
              title={
                <Space>
                  <BankOutlined />
                  <span>{indiaPack ? 'Payroll bank export (UAE WPS not applicable)' : 'UAE WPS / SIF export'}</span>
                </Space>
              }
            >
              {indiaPack ? (
                <Alert
                  type="info"
                  showIcon
                  style={{ marginBottom: 12 }}
                  message="India NEFT / bank credit export"
                  description="Use Payroll period → NEFT export. Employees need bank account number + IFSC in bank branch code. WPS/SIF remains UAE-only."
                />
              ) : null}
              <Paragraph type="secondary" style={{ marginTop: 0 }}>
                {indiaPack
                  ? 'Configure debit account / company name for the NEFT CSV header. Leave UAE WPS fields blank.'
                  : 'Required for payroll period WPS download (MOL SCR + EDR SIF). Employee rows need IBAN and WPS person code (or Emirates ID).'}
              </Paragraph>
              {indiaPack ? (
                <Row gutter={16}>
                  <Col xs={24} md={8}>
                    <Form.Item name="hrms.india.neft.payment_mode" label="Payment mode" initialValue="NEFT">
                      <Select
                        options={[
                          { value: 'NEFT', label: 'NEFT' },
                          { value: 'RTGS', label: 'RTGS' },
                          { value: 'IMPS', label: 'IMPS' },
                        ]}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={8}>
                    <Form.Item name="hrms.india.neft.debit_account" label="Debit account (company)">
                      <Input placeholder="Company bank account number" />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={8}>
                    <Form.Item name="hrms.india.neft.company_name" label="Company name on file">
                      <Input placeholder="Account name / school legal name" />
                    </Form.Item>
                  </Col>
                </Row>
              ) : (
                <Row gutter={16}>
                  <Col xs={24} md={12}>
                    <Form.Item
                      name="hrms.wps.employer_unique_id"
                      label="Employer unique ID (MOL)"
                      rules={[{ required: false }]}
                      help="Mandatory before WPS export will run."
                    >
                      <Input placeholder="Employer Unique ID" />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item
                      name="hrms.wps.payer_bank"
                      label="Payer bank (SCR)"
                      help="Short bank code / routing used on the SCR control record."
                    >
                      <Input placeholder="e.g. bank routing code" />
                    </Form.Item>
                  </Col>
                </Row>
              )}
            </Card>
          </Col>

          {indiaPack ? (
            <Col xs={24}>
              <Card loading={isLoading} title="India statutory deductions (EPF / ESI / PT / TDS)">
                <Alert
                  type="warning"
                  showIcon
                  style={{ marginBottom: 12 }}
                  message="Operational rates — confirm with Finance / Legal"
                  description="Auto-calc runs on payroll compute when enabled. Manual employee salary components for the same code win. TDS slab mode projects FY income (April–March), applies regime slabs + 87A rebate + cess. Employer EPF/ESI appear on payslip and GL handoff (not deducted from net). Education student leave is separate from HR leave."
                />
                <Row gutter={16}>
                  <Col xs={24} md={8}>
                    <Form.Item name="hrms.compliance.india_state" label="India state (Shops Act overlay)" initialValue="CG">
                      <Select
                        options={[
                          { value: 'CG', label: 'Chhattisgarh (CG)' },
                          { value: 'DEFAULT', label: 'Central / default baseline' },
                        ]}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={8}>
                    <Form.Item
                      name="hrms.leave.school_board_confirmed"
                      label="School/board leave confirmed"
                      initialValue="false"
                      help="Set Yes only after school authority confirms EL/CL/SL (not CBSE invention)."
                    >
                      <Select
                        options={[
                          { value: 'true', label: 'Yes — confirmed with school' },
                          { value: 'false', label: 'No — still operational seeds' },
                        ]}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={8}>
                    <Form.Item name="hrms.india.statutory_auto_calc" label="Auto-calc on payroll" initialValue="true">
                      <Select
                        options={[
                          { value: 'true', label: 'Enabled' },
                          { value: 'false', label: 'Disabled' },
                        ]}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={6}>
                    <Form.Item name="hrms.india.epf.enabled" label="EPF" initialValue="true">
                      <Select options={[{ value: 'true', label: 'On' }, { value: 'false', label: 'Off' }]} />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={6}>
                    <Form.Item name="hrms.india.epf.employee_rate" label="EPF employee rate" initialValue="0.12">
                      <Input placeholder="0.12" />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={6}>
                    <Form.Item name="hrms.india.epf.wage_ceiling" label="EPF wage ceiling (₹)" initialValue="15000">
                      <Input placeholder="15000" />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={6}>
                    <Form.Item name="hrms.eosb.include_da_in_wages" label="Include DA in gratuity wages" initialValue="true">
                      <Select options={[{ value: 'true', label: 'Yes' }, { value: 'false', label: 'Basic only' }]} />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={6}>
                    <Form.Item name="hrms.india.esi.enabled" label="ESI" initialValue="true">
                      <Select options={[{ value: 'true', label: 'On' }, { value: 'false', label: 'Off' }]} />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={6}>
                    <Form.Item name="hrms.india.esi.employee_rate" label="ESI employee rate" initialValue="0.0075">
                      <Input placeholder="0.0075" />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={6}>
                    <Form.Item name="hrms.india.esi.wage_ceiling" label="ESI wage ceiling (₹)" initialValue="21000">
                      <Input placeholder="21000" />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={6}>
                    <Form.Item name="hrms.india.pt.enabled" label="Professional tax" initialValue="true">
                      <Select options={[{ value: 'true', label: 'On' }, { value: 'false', label: 'Off' }]} />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={6}>
                    <Form.Item name="hrms.india.tds.enabled" label="TDS" initialValue="true">
                      <Select options={[{ value: 'true', label: 'On' }, { value: 'false', label: 'Off' }]} />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={6}>
                    <Form.Item name="hrms.india.tds.mode" label="TDS mode" initialValue="slab">
                      <Select
                        options={[
                          { value: 'slab', label: 'Full slab engine (FY projected)' },
                          { value: 'fixed', label: 'Fixed (employee TDS head)' },
                          { value: 'percent_gross', label: '% of gross' },
                        ]}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={6}>
                    <Form.Item name="hrms.india.tds.default_regime" label="Default tax regime" initialValue="NEW">
                      <Select
                        options={[
                          { value: 'NEW', label: 'New regime' },
                          { value: 'OLD', label: 'Old regime' },
                        ]}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={6}>
                    <Form.Item name="hrms.india.tds.percent_gross" label="TDS % of gross (legacy)" initialValue="0">
                      <Input placeholder="only if mode = % of gross" />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item
                      name="hrms.gl.epf_employer_expense_account_id"
                      label="EPF employer expense account"
                      help="Debited with EPF_ER on GL handoff."
                    >
                      <Select allowClear showSearch optionFilterProp="label" options={coaOptions} />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item
                      name="hrms.gl.epf_employer_liability_account_id"
                      label="EPF employer liability account"
                      help="Credited until remitted to EPFO."
                    >
                      <Select allowClear showSearch optionFilterProp="label" options={coaOptions} />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item name="hrms.gl.esi_employer_expense_account_id" label="ESI employer expense account">
                      <Select allowClear showSearch optionFilterProp="label" options={coaOptions} />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item name="hrms.gl.esi_employer_liability_account_id" label="ESI employer liability account">
                      <Select allowClear showSearch optionFilterProp="label" options={coaOptions} />
                    </Form.Item>
                  </Col>
                </Row>
              </Card>
            </Col>
          ) : null}
        </Row>
      </Form>

      <Card size="small" style={{ marginTop: 16 }} title="Recommended setup checklist">
        <Row gutter={[16, 8]}>
          <Col xs={24} md={12}>
            <ol style={{ margin: 0, paddingLeft: 18 }}>
              <li>
                Save this company policy (or {indiaPack ? 'Apply pack defaults' : 'Apply UAE defaults'} first).
              </li>
              <li>
                Create <Link to="/hr/holidays">holiday calendars</Link> for the current year.
              </li>
              <li>
                Define <Link to="/hr/locations">locations</Link> and <Link to="/hr/shifts">shifts</Link>.
              </li>
            </ol>
          </Col>
          <Col xs={24} md={12}>
            <ol start={4} style={{ margin: 0, paddingLeft: 18 }}>
              <li>
                Configure <Link to="/hr/grades">grades</Link> and{' '}
                <Link to="/hr/salary-heads">salary heads</Link> (OT / EOSB base flags).
              </li>
              <li>
                Map GL accounts above, then open a <Link to="/hr/payroll">payroll period</Link>.
              </li>
              <li>
                Configure Platform approval workflows for leave, OT, loans, payroll, and EOSB. Review{' '}
                <Link to="/hr/audit-log">HRMS audit log</Link> after bulk accrual runs.
              </li>
            </ol>
          </Col>
        </Row>
      </Card>
      <WorkWeekModal open={workWeeksOpen} onClose={() => setWorkWeeksOpen(false)} />
    </WorkspaceLayout>
  );
};

export default HrmsSetupPage;
